import type { SnapshotRecoveryCopy } from '@/lib/offline/owned-snapshot-store'
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react'
import { useConfirmDialog } from '@/hooks/useConfirmDialog'
import { useRequestScope } from '@/hooks/useRequestScope'
import {
  ledgerSnapshotStorage,
  preserveOfflineWorkForReload,
} from '@/lib/offline/ledger'
import { saveBlob } from '@/lib/utils/download'
import { useAuth } from '@/store/auth.store'
import { useQuery, useQueryClient } from '@tanstack/react-query'

export function useOfflineRecoveryCopies() {
  const confirmation = useConfirmDialog()
  const owner = useAuth(state => state.user?.id ?? null)
  const scope = useRequestScope()
  const client = useQueryClient()
  const status = useSyncExternalStore(
    ledgerSnapshotStorage.subscribe,
    () => ledgerSnapshotStorage.recoveryStatus(owner),
    () => null,
  )
  const queryKey = ['offline-recovery-copies', owner, scope.generation] as const
  const query = useQuery({
    queryKey,
    enabled: !!owner,
    retry: false,
    queryFn: async () => {
      scope.assertCurrent()
      const copies = await ledgerSnapshotStorage.readRecoveries(owner!)
      scope.assertCurrent()
      return copies
    },
  })
  useEffect(
    () =>
      ledgerSnapshotStorage.subscribe(() => {
        void client.invalidateQueries({ queryKey: ['offline-recovery-copies'] })
      }),
    [client],
  )
  const running = useRef(false)
  const [busy, setBusy] = useState(false)
  const [feedback, setFeedback] = useState<{
    scope: typeof scope
    message?: string
    busy: boolean
  }>()
  const run = useCallback(
    async (action: () => Promise<void>) => {
      if (running.current) return
      running.current = true
      setBusy(true)
      try {
        scope.assertCurrent()
        setFeedback({ scope, busy: true })
        await action()
        scope.assertCurrent()
        setFeedback({ scope, busy: false })
      } catch (error) {
        try {
          scope.assertCurrent()
        } catch {
          return
        }
        setFeedback({
          scope,
          busy: false,
          message:
            error instanceof Error
              ? error.message
              : 'The recovery action could not be confirmed.',
        })
      } finally {
        running.current = false
        setBusy(false)
      }
    },
    [scope],
  )
  const assertCopy = (copy: SnapshotRecoveryCopy) => {
    scope.assertCurrent()
    if (!owner || copy.ownerId !== owner)
      throw new Error('This recovery copy belongs to another account.')
  }
  return {
    dialog: confirmation.dialog,
    copies: owner ? (query.data ?? []) : [],
    status,
    error:
      feedback?.scope === scope && feedback.message
        ? feedback.message
        : query.isError
          ? 'Recovery copies could not be loaded. Keep unsaved input and try again.'
          : undefined,
    busy,
    refresh: () =>
      run(async () => {
        await query.refetch({ throwOnError: true })
      }),
    reload: () =>
      run(async () => {
        await preserveOfflineWorkForReload(scope.assertCurrent)
        scope.assertCurrent()
        window.location.reload()
      }),
    download: (copy: SnapshotRecoveryCopy) =>
      run(async () => {
        assertCopy(copy)
        const current = (
          await ledgerSnapshotStorage.readRecoveries(copy.ownerId)
        ).find(row => row.operationId === copy.operationId)
        assertCopy(copy)
        if (!current || JSON.stringify(current) !== JSON.stringify(copy))
          throw new Error(
            'This recovery copy has changed or was removed. Refresh the recovery list.',
          )
        await saveBlob(
          new Blob(
            [
              JSON.stringify(
                {
                  notice:
                    'Local recovery evidence only. Check the current record before creating or resending a change.',
                  ...copy,
                },
                null,
                2,
              ),
            ],
            { type: 'application/json' },
          ),
          `offline-recovery-${copy.operationId}.json`,
          scope.assertCurrent,
        )
      }),
    remove: (copy: SnapshotRecoveryCopy) =>
      run(async () => {
        assertCopy(copy)
        if (
          !(await confirmation.confirm({
            title: 'Remove recovery copy?',
            description:
              'This removes only the local recovery copy. It does not cancel any change already sent.',
            confirmText: 'Remove copy',
            variant: 'destructive',
          }))
        )
          return
        assertCopy(copy)
        await ledgerSnapshotStorage.removeRecovery(copy)
        scope.assertCurrent()
        await client.invalidateQueries({
          queryKey: ['offline-recovery-copies', owner],
        })
      }),
  }
}
